import { useEffect, useMemo, useState } from 'react';
import { Info, RefreshCw } from 'lucide-react';
import {
  type WaterSavedChartBucket,
  WaterSavedMonthlyChart,
} from '../components/WaterSavedMonthlyChart.tsx';
import { WaterSavedSensorTable } from '../components/WaterSavedSensorTable.tsx';
import { WaterSavedValidationChart } from '../components/WaterSavedValidationChart.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip.tsx';
import { Permission, type WaterSavedPeriodType, waterSavedPeriodWindow } from '@metron/shared';
import { usePermissions } from '../hooks/usePermissions.ts';
import {
  useRecomputeWaterSaved,
  useWaterSavedMonthDetail,
  useWaterSavedMonthly,
  useWaterSavedPeriods,
  useWaterSavedRangeSensors,
  useWaterSavedSensors,
  useWaterSavedValidation,
} from '../hooks/useWaterSaved.ts';

type TimeWindow = 'monthly' | WaterSavedPeriodType;

const TIME_WINDOWS: { value: TimeWindow; label: string; noun: string }[] = [
  { value: 'monthly', label: 'Monthly', noun: 'Month' },
  { value: 'quarter', label: 'Quarterly', noun: 'Quarter' },
  { value: 'year', label: 'Yearly', noun: 'Year' },
];

const GALLONS_SAVED_TOOLTIP =
  'Each fix contributes a 30-day run-rate projection: the drop in average flow ' +
  'after repair, projected over 30 days. Totals are sums of those projections, ' +
  'not metered volume over the period.';

function formatMonthLong(month: string): string {
  const [year, mon] = month.split('-');
  const date = new Date(parseInt(year), parseInt(mon) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatMonthShort(month: string): string {
  const [year, mon] = month.split('-');
  const date = new Date(parseInt(year), parseInt(mon) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

const fmtGal = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

interface StatCard {
  label: string;
  value: string;
  tooltip?: string;
}

export function WaterSavedPage() {
  const { hasPermission, isViewer } = usePermissions();
  const canRefreshMetrics = hasPermission(Permission.REFRESH_METRICS);
  // Temporarily disable recompute in production
  const isLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // State for time window, selected bucket, and sensor
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('monthly');
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedPeriodStart, setSelectedPeriodStart] = useState<string | null>(null);
  const [selectedSensorId, setSelectedSensorId] = useState<number | null>(null);
  const [sensorPage, setSensorPage] = useState(1);
  const [sensorLimit, setSensorLimit] = useState(20);

  const isMonthly = timeWindow === 'monthly';
  const windowNoun = TIME_WINDOWS.find((w) => w.value === timeWindow)!.noun;

  // Fetch monthly data (now includes meter stats)
  const {
    data: monthlyData,
    isLoading: isLoadingMonthly,
    error: monthlyError,
  } = useWaterSavedMonthly();

  // Fetch precomputed period rollups when a quarter/year window is active
  const {
    data: periodsData,
    isLoading: isLoadingPeriods,
    error: periodsError,
  } = useWaterSavedPeriods(isMonthly ? null : timeWindow);

  // Auto-select the most recent month when data loads
  useEffect(() => {
    if (monthlyData?.months && monthlyData.months.length > 0 && !selectedMonth) {
      // Months are in YYYY-MM format, sort descending to get most recent
      const sortedMonths = [...monthlyData.months].sort((a, b) => b.month.localeCompare(a.month));
      setSelectedMonth(sortedMonths[0].month);
    }
  }, [monthlyData, selectedMonth]);

  // Auto-select the most recent period when the window changes or data loads
  // (the API returns periods newest first)
  useEffect(() => {
    if (isMonthly) return;
    const periods = periodsData?.periods ?? [];
    if (periods.length === 0) return;
    if (!selectedPeriodStart || !periods.some((p) => p.periodStart === selectedPeriodStart)) {
      setSelectedPeriodStart(periods[0].periodStart);
    }
  }, [isMonthly, periodsData, selectedPeriodStart]);

  const selectedPeriod = isMonthly
    ? null
    : periodsData?.periods.find((p) => p.periodStart === selectedPeriodStart) ?? null;

  // Chart buckets: monthly rows or server-side period rollups, never
  // aggregated client-side (the mistake that sank #796)
  const chartBuckets = useMemo((): WaterSavedChartBucket[] => {
    if (isMonthly) {
      return (monthlyData?.months ?? []).map((m) => ({
        key: m.month,
        label: formatMonthShort(m.month),
        isComplete: true,
        totalGallonsSaved: m.totalGallonsSaved,
        fixCount: m.metersWithFixedLeaks,
        avgGallonsSaved: m.avgGallonsSaved,
        avgPctSaved: m.avgPctSaved,
      }));
    }
    return (periodsData?.periods ?? []).map((p) => ({
      key: p.periodStart,
      label: p.isComplete ? p.label : `${p.label} (partial)`,
      isComplete: p.isComplete,
      totalGallonsSaved: p.totalGallonsSaved,
      fixCount: p.fixedLeakEpisodes,
      avgGallonsSaved: p.avgGallonsSaved,
      avgPctSaved: p.avgPctSaved,
    }));
  }, [isMonthly, monthlyData, periodsData]);

  // Fetch selected month detail (monthly mode only)
  const {
    data: monthDetail,
    isLoading: isLoadingMonthDetail,
  } = useWaterSavedMonthDetail(isMonthly ? selectedMonth : null);

  // Sensor drill-down: per-month endpoint in monthly mode, date range otherwise
  const {
    data: monthSensorsData,
    isLoading: isLoadingMonthSensors,
    error: monthSensorsError,
  } = useWaterSavedSensors(isMonthly ? selectedMonth : null, sensorPage, sensorLimit);

  const rangeFrom = selectedPeriod?.periodStart ?? null;
  const rangeTo = selectedPeriod
    ? waterSavedPeriodWindow(selectedPeriod.periodType, selectedPeriod.periodStart)
      .periodEndExclusive
    : null;
  const {
    data: rangeSensorsData,
    isLoading: isLoadingRangeSensors,
    error: rangeSensorsError,
  } = useWaterSavedRangeSensors(rangeFrom, rangeTo, sensorPage, sensorLimit);

  const sensorsData = isMonthly ? monthSensorsData : rangeSensorsData;
  const isLoadingSensors = isMonthly ? isLoadingMonthSensors : isLoadingRangeSensors;
  const sensorsError = isMonthly ? monthSensorsError : rangeSensorsError;

  // Fetch validation data for selected sensor
  const {
    data: validationData,
    isLoading: isLoadingValidation,
    error: validationError,
  } = useWaterSavedValidation(selectedSensorId);

  // Recompute mutation
  const { mutate: recompute, isPending: isRecomputing } = useRecomputeWaterSaved();

  // Handle time-window change
  const handleWindowChange = (next: TimeWindow) => {
    setTimeWindow(next);
    setSelectedPeriodStart(null);
    setSelectedSensorId(null);
    setSensorPage(1);
  };

  // Handle bucket selection from the chart
  const handleBucketClick = (key: string) => {
    if (isMonthly) setSelectedMonth(key);
    else setSelectedPeriodStart(key);
    setSelectedSensorId(null);
    setSensorPage(1);
  };

  // Handle sensor selection
  const handleSensorClick = (sensorId: number) => {
    setSelectedSensorId(sensorId);
  };

  // Handle recompute for selected month (monthly mode only)
  const handleRecompute = () => {
    if (!selectedMonth) return;
    recompute({ month: selectedMonth });
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setSensorPage(page);
  };

  // Handle limit change
  const handleLimitChange = (limit: number) => {
    setSensorLimit(limit);
    setSensorPage(1); // Reset to first page when changing limit
  };

  // Close validation chart
  const handleCloseValidation = () => {
    setSelectedSensorId(null);
  };

  // Detail section state per mode
  const detailTitle = isMonthly
    ? (selectedMonth ? formatMonthLong(selectedMonth) : null)
    : selectedPeriod?.label ?? null;
  const detailComputedAt = isMonthly
    ? monthDetail?.summary.computedAt ?? null
    : selectedPeriod?.computedAt ?? null;
  const isLoadingDetail = isMonthly ? isLoadingMonthDetail : isLoadingPeriods;
  const hasDetail = isMonthly ? !!monthDetail : !!selectedPeriod;
  const sensorTableLabel = isMonthly
    ? (selectedMonth ? formatMonthLong(selectedMonth) : '')
    : selectedPeriod?.label ?? '';

  // Summary cards per mode. Monthly keeps its existing eight; quarter/year
  // adds the episode/distinct-meter split and the true median, all computed
  // server-side over the full window.
  const cards = useMemo((): StatCard[] => {
    if (isMonthly) {
      if (!monthDetail) return [];
      const s = monthDetail.summary;
      return [
        { label: 'Active Meters', value: s.totalMeters.toLocaleString() },
        { label: 'Active Residential', value: s.residentialMeters.toLocaleString() },
        { label: 'Meters with Leaks', value: s.metersWithLeaks.toLocaleString() },
        { label: 'Fixed Leaks', value: s.metersWithFixedLeaks.toLocaleString() },
        { label: 'Avg Gal Saved Per Fix', value: fmtGal(s.avgGallonsSaved) },
        {
          label: 'Gallons Saved',
          value: fmtGal(s.totalGallonsSaved),
          tooltip: GALLONS_SAVED_TOOLTIP,
        },
        {
          label: 'Avg Gal Saved Per Residential',
          value: s.residentialMeters > 0 ? fmtGal(s.totalGallonsSaved / s.residentialMeters) : '—',
        },
        {
          label: 'Avg Gal Saved Per Meter',
          value: s.totalMeters > 0 ? fmtGal(s.totalGallonsSaved / s.totalMeters) : '—',
        },
      ];
    }
    if (!selectedPeriod) return [];
    const p = selectedPeriod;
    return [
      { label: 'Active Meters', value: p.totalMeters.toLocaleString() },
      { label: 'Active Residential', value: p.residentialMeters.toLocaleString() },
      { label: 'Meters with Leaks', value: p.metersWithLeaks.toLocaleString() },
      {
        label: 'Leak Fixes',
        value: p.fixedLeakEpisodes.toLocaleString(),
        tooltip: 'Fix episodes across the period. A meter fixed in several months counts once ' +
          'per month, so this can exceed Meters Fixed.',
      },
      { label: 'Meters Fixed', value: p.metersWithFixedLeaks.toLocaleString() },
      { label: 'Avg Gal Saved Per Fix', value: fmtGal(p.avgGallonsSaved) },
      {
        label: 'Median Gal Saved Per Fix',
        value: p.medianGallonsSaved !== null ? fmtGal(p.medianGallonsSaved) : '—',
      },
      {
        label: 'Gallons Saved',
        value: fmtGal(p.totalGallonsSaved),
        tooltip: GALLONS_SAVED_TOOLTIP,
      },
      {
        label: 'Avg Gal Saved Per Residential',
        value: p.residentialMeters > 0 ? fmtGal(p.totalGallonsSaved / p.residentialMeters) : '—',
      },
      {
        label: 'Avg Gal Saved Per Meter',
        value: p.totalMeters > 0 ? fmtGal(p.totalGallonsSaved / p.totalMeters) : '—',
      },
    ];
  }, [isMonthly, monthDetail, selectedPeriod]);

  return (
    <div className='container mx-auto px-4 py-8 space-y-6'>
      <div>
        <h1 className='text-3xl font-bold'>Water Saved Dashboard</h1>
        <p className='text-muted-foreground'>
          Track water savings from fixed leaks across the fleet
        </p>
      </div>

      {/* Introduction */}
      <div className='text-sm text-muted-foreground space-y-2'>
        <p>
          This dashboard tracks water savings achieved by detecting and fixing leaks across the
          meter fleet. Water saved is calculated by comparing average water usage during the leak
          period to usage after the leak was repaired.
        </p>
        {canRefreshMetrics && (
          <p>
            <span className='font-medium text-foreground'>Admin:</span>{' '}
            Use the Recompute button on a selected month to recalculate its water saved data. This
            analyzes all meters with leaks that were subsequently fixed and calculates the water
            savings.
          </p>
        )}
      </div>

      {/* Time-window toggle */}
      <div className='flex gap-2' role='group' aria-label='Time window'>
        {TIME_WINDOWS.map((w) => (
          <Button
            key={w.value}
            size='sm'
            variant={w.value === timeWindow ? 'default' : 'outline'}
            aria-pressed={w.value === timeWindow}
            onClick={() => handleWindowChange(w.value)}
          >
            {w.label}
          </Button>
        ))}
      </div>

      {/* Water Saved Chart */}
      <WaterSavedMonthlyChart
        data={chartBuckets}
        periodNoun={windowNoun}
        fixCountLabel={isMonthly ? 'Fixed Leaks' : 'Leak Fixes'}
        onBucketClick={handleBucketClick}
        isLoading={isMonthly ? isLoadingMonthly : isLoadingPeriods}
        error={(isMonthly ? monthlyError : periodsError)?.message ?? null}
      />

      {/* Selected Bucket Detail */}
      {detailTitle && (
        <div className='space-y-6'>
          {/* Header with Recompute Button */}
          <div className='flex items-center justify-between'>
            <div>
              <h2 className='text-xl font-semibold'>
                {detailTitle}
                {!isMonthly && selectedPeriod && !selectedPeriod.isComplete && (
                  <span className='ml-2 text-sm font-normal text-muted-foreground'>
                    ({selectedPeriod.monthCount} of {selectedPeriod.expectedMonthCount}{' '}
                    months computed)
                  </span>
                )}
              </h2>
              {!isLoadingDetail && hasDetail && (
                <p className='text-xs text-muted-foreground'>
                  Last computed:{' '}
                  {detailComputedAt ? new Date(detailComputedAt).toLocaleString() : 'Never'}
                </p>
              )}
            </div>
            {canRefreshMetrics && isMonthly && (
              <Button
                onClick={handleRecompute}
                disabled={isRecomputing || isLoadingMonthDetail || !isLocalhost}
                variant='outline'
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRecomputing ? 'animate-spin' : ''}`} />
                {isRecomputing ? 'Recomputing...' : 'Recompute'}
              </Button>
            )}
          </div>

          {/* Summary Cards */}
          {isLoadingDetail
            ? (
              <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
                {[...Array(8)].map((_, i) => (
                  <Card key={i}>
                    <CardHeader className='pb-2'>
                      <CardDescription className='h-4 w-24 bg-muted animate-pulse rounded' />
                      <div className='h-8 w-20 bg-muted animate-pulse rounded mt-2' />
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )
            : cards.length > 0 && (
              <TooltipProvider>
                <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
                  {cards.map((card) => (
                    <Card key={card.label}>
                      <CardHeader className='pb-2'>
                        <CardDescription>
                          {card.tooltip
                            ? (
                              <Tooltip>
                                <TooltipTrigger className='inline-flex items-center gap-1 cursor-help'>
                                  {card.label}
                                  <Info className='h-3 w-3' />
                                </TooltipTrigger>
                                <TooltipContent className='max-w-xs'>
                                  {card.tooltip}
                                </TooltipContent>
                              </Tooltip>
                            )
                            : card.label}
                        </CardDescription>
                        <CardTitle className='text-2xl'>{card.value}</CardTitle>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </TooltipProvider>
            )}

          {/* Validation chart + sensors table are hidden from Viewers */}
          {!isViewer && (
            <>
              {/* Validation Chart (if sensor selected) */}
              {selectedSensorId && (
                <WaterSavedValidationChart
                  data={validationData ?? null}
                  month={isMonthly ? selectedMonth : null}
                  isLoading={isLoadingValidation}
                  error={validationError?.message ?? null}
                  onClose={handleCloseValidation}
                />
              )}

              {/* Sensors Table */}
              {sensorsData && (
                <WaterSavedSensorTable
                  sensors={sensorsData.sensors}
                  total={sensorsData.total}
                  page={sensorsData.page}
                  limit={sensorsData.limit}
                  label={sensorTableLabel}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                  onSensorClick={handleSensorClick}
                  isLoading={isLoadingSensors}
                  error={sensorsError?.message ?? null}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Empty State */}
      {!isLoadingMonthly && (!monthlyData || monthlyData.months.length === 0) && (
        <Card>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <p className='text-lg font-medium text-muted-foreground'>
              No water saved data available
            </p>
            <p className='text-sm text-muted-foreground mt-1'>
              Data needs to be computed for each month to appear here
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
